// src/pages/finance-portal/inventory/PODetail.js
// PO detail: summary, lines, approval actions, receiving tab, matching tab.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    Grid, IconButton, Paper, Skeleton, Stack, Tab, Table, TableBody,
    TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK, formatCurrency, formatDate,
    PO_STATUS_COLORS, MATCH_STATUS_COLORS, statusChip,
} from './components/inventoryFormatters';

export default function PODetail() {
    const { poId } = useParams();
    const navigate = useNavigate();
    const [po, setPO] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState(0);
    const [actionDialog, setActionDialog] = useState({ open: false, action: null, comments: '' });
    const [acting, setActing] = useState(false);
    const [matchDialog, setMatchDialog] = useState({ open: false, bill_id: '', result: null });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/purchase-orders/${poId}`);
            if (res.data?.success) setPO(res.data.data);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [poId]);

    useEffect(() => { load(); }, [load]);

    const runAction = async (action, extra = {}) => {
        setActing(true);
        try {
            await axiosInstance.post(`/api/finance/inventory/purchase-orders/${poId}/${action}`, extra);
            setActionDialog({ open: false, action: null, comments: '' });
            await load();
        } catch (e) {
            alert(e.response?.data?.message || 'Action failed');
        } finally {
            setActing(false);
        }
    };

    const runMatch = async () => {
        try {
            const res = await axiosInstance.post(
                `/api/finance/inventory/purchase-orders/${poId}/match-bill`,
                { bill_id: matchDialog.bill_id },
            );
            setMatchDialog((d) => ({ ...d, result: res.data?.data }));
        } catch (e) {
            alert(e.response?.data?.message || 'Match failed');
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}><Skeleton height={80} /><Skeleton variant="rectangular" height={260} sx={{ mt: 2 }} /></Box>
        );
    }
    if (error || !po) return <Box sx={{ p: 3 }}><Alert severity="error">{error || 'PO not found'}</Alert></Box>;

    const canEdit = ['DRAFT', 'PENDING_APPROVAL', 'REJECTED'].includes(po.status);
    const canSubmit = po.status === 'DRAFT';
    const canApprove = ['DRAFT', 'PENDING_APPROVAL'].includes(po.status);
    const canSend = po.status === 'APPROVED';
    const canReceive = ['APPROVED', 'SENT', 'ACKNOWLEDGED', 'PARTIALLY_RECEIVED'].includes(po.status);

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate('/finance-portal/inventory/purchase-orders')}><ArrowBackIcon /></IconButton>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.55rem', fontWeight: 700, color: '#0f172a' }}>
                                {po.po_number}
                            </Typography>
                            <Chip size="small" label={po.status?.replace('_', ' ')}
                                style={statusChip(po.status, PO_STATUS_COLORS)}
                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                            {po.is_auto_generated && (
                                <Chip size="small" label="AUTO-GENERATED" sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, bgcolor: '#e0e7ff', color: '#3730a3' }} />
                            )}
                        </Stack>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.88rem', color: '#64748b' }}>
                            {po.vendor_name} · {po.po_type} · {po.order_date && formatDate(po.order_date)}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    {canEdit && (
                        <Button variant="outlined" startIcon={<EditIcon />}
                            onClick={() => navigate(`/finance-portal/inventory/purchase-orders/${poId}/edit`)}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Edit</Button>
                    )}
                    {canSubmit && (
                        <Button variant="outlined" onClick={() => runAction('submit')}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Submit for Approval</Button>
                    )}
                    {canApprove && (
                        <Button variant="contained" color="success" startIcon={<CheckIcon />}
                            onClick={() => setActionDialog({ open: true, action: 'approve', comments: '' })}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Approve</Button>
                    )}
                    {po.status === 'PENDING_APPROVAL' && (
                        <Button variant="outlined" color="error" startIcon={<CloseIcon />}
                            onClick={() => setActionDialog({ open: true, action: 'reject', comments: '' })}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>Reject</Button>
                    )}
                    {canSend && (
                        <Button variant="contained" startIcon={<SendIcon />}
                            onClick={() => runAction('send')}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                                  bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                            Send to Vendor
                        </Button>
                    )}
                    {canReceive && (
                        <Button variant="contained" startIcon={<MoveToInboxIcon />}
                            onClick={() => navigate(`/finance-portal/inventory/receiving/new?po=${poId}`)}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                                  bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
                            Receive Goods
                        </Button>
                    )}
                </Stack>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Total Amount</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.3rem', fontWeight: 700 }}>
                                {formatCurrency(po.total_amount, po.currency)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Received</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.3rem', fontWeight: 700 }}>
                                {formatCurrency(po.received_value, po.currency)}
                                <Typography component="span" sx={{ fontSize: '0.85rem', color: '#64748b', ml: 1 }}>
                                    ({Number(po.received_percentage || 0).toFixed(0)}%)
                                </Typography>
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Billed</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.3rem', fontWeight: 700 }}>
                                {formatCurrency(po.billed_value, po.currency)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b' }}>Expected Delivery</Typography>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.05rem', fontWeight: 600 }}>
                                {po.expected_delivery_date ? formatDate(po.expected_delivery_date) : '—'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)}
                    sx={{ borderBottom: '1px solid #e2e8f0',
                          '& .MuiTab-root': { fontFamily: FONT_FAMILY, textTransform: 'none', fontWeight: 600 } }}>
                    <Tab label={`Lines (${po.lines?.length || 0})`} />
                    <Tab label={`Receipts (${po.receipts?.length || 0})`} />
                    <Tab label="3-Way Match" />
                    <Tab label={`Approvals (${po.approvals?.length || 0})`} />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {tab === 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Description</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Ordered</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Received</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Billed</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Unit Price</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Line Total</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Match</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(po.lines || []).map((ln) => (
                                    <TableRow key={ln.id}>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{ln.sku || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>
                                            {ln.product_name || ln.description}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{ln.ordered_qty}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{ln.received_qty}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{ln.billed_qty}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{formatCurrency(ln.unit_price, po.currency)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">{formatCurrency(ln.line_total, po.currency)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={ln.match_status?.replace('_', ' ')}
                                                style={statusChip(ln.match_status, MATCH_STATUS_COLORS)}
                                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {tab === 1 && (
                        <>
                            {(po.receipts || []).length === 0 ? (
                                <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', textAlign: 'center', py: 4 }}>
                                    No receipts recorded yet.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Receipt #</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Date</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Value</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {po.receipts.map((r) => (
                                            <TableRow key={r.id} hover
                                                onClick={() => navigate(`/finance-portal/inventory/receiving/${r.id}`)}
                                                sx={{ cursor: 'pointer' }}>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{r.receipt_number}</TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY }}>{formatDate(r.receipt_date)}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={r.status}
                                                        sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                                    {formatCurrency(r.total_received_value, r.currency)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </>
                    )}

                    {tab === 2 && (
                        <Box>
                            <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', mb: 2 }}>
                                Match this PO to a vendor bill to verify ordered vs. received vs. billed quantities and prices.
                            </Typography>
                            <Button variant="contained" startIcon={<CompareArrowsIcon />}
                                onClick={() => setMatchDialog({ open: true, bill_id: '', result: null })}
                                sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                                      bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                                Match to Bill
                            </Button>
                            {po.bill && (
                                <Alert severity="info" sx={{ mt: 3 }}>
                                    Already linked to Bill {po.bill.bill_number} — {formatCurrency(po.bill.total_amount, po.bill.currency)}
                                </Alert>
                            )}
                        </Box>
                    )}

                    {tab === 3 && (
                        <>
                            {(po.approvals || []).length === 0 ? (
                                <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', textAlign: 'center', py: 4 }}>
                                    No approval actions yet.
                                </Typography>
                            ) : (
                                <Stack spacing={1}>
                                    {po.approvals.map((a) => (
                                        <Paper key={a.id} variant="outlined" sx={{ p: 2 }}>
                                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }}>
                                                {a.decision} · step {a.step_order} · user #{a.approver_user_id}
                                            </Typography>
                                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.8rem', color: '#64748b' }}>
                                                {a.created_at}
                                            </Typography>
                                            {a.comments && (
                                                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', mt: 1 }}>
                                                    {a.comments}
                                                </Typography>
                                            )}
                                        </Paper>
                                    ))}
                                </Stack>
                            )}
                        </>
                    )}
                </Box>
            </Paper>

            {/* Approve / reject dialog */}
            <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, action: null, comments: '' })}>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>
                    {actionDialog.action === 'approve' ? 'Approve PO' : 'Reject PO'}
                </DialogTitle>
                <DialogContent>
                    <TextField fullWidth multiline rows={3} label="Comments (optional)" sx={{ mt: 1, minWidth: 400 }}
                        value={actionDialog.comments}
                        onChange={(e) => setActionDialog((d) => ({ ...d, comments: e.target.value }))} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setActionDialog({ open: false, action: null, comments: '' })}>Cancel</Button>
                    <Button variant="contained" disabled={acting}
                        color={actionDialog.action === 'approve' ? 'success' : 'error'}
                        onClick={() => runAction(actionDialog.action, { comments: actionDialog.comments })}>
                        {acting ? 'Working…' : actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Match dialog */}
            <Dialog open={matchDialog.open} onClose={() => setMatchDialog({ open: false, bill_id: '', result: null })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>3-Way Match: PO ↔ Receipt ↔ Bill</DialogTitle>
                <DialogContent>
                    <TextField fullWidth sx={{ mt: 1 }} type="number" label="AP Bill ID"
                        value={matchDialog.bill_id}
                        onChange={(e) => setMatchDialog((d) => ({ ...d, bill_id: e.target.value }))} />
                    {matchDialog.result && (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity={matchDialog.result.overall_status === 'MATCHED' ? 'success' : 'warning'} sx={{ mb: 2 }}>
                                Overall status: <b>{matchDialog.result.overall_status}</b>
                            </Alert>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Ordered</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Received</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Billed</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {matchDialog.result.lines.map((l, i) => (
                                        <TableRow key={i}>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>{l.sku || '—'}</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{l.ordered_qty}</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{l.received_qty}</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{l.billed_qty}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={l.status?.replace('_', ' ')}
                                                    style={statusChip(l.status, MATCH_STATUS_COLORS)}
                                                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMatchDialog({ open: false, bill_id: '', result: null })}>Close</Button>
                    <Button variant="contained" disabled={!matchDialog.bill_id} onClick={runMatch}
                        sx={{ bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                        Run Match
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
